import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { Logo, Wordmark } from "@/components/Logo";
import { Reveal } from "@/components/Reveal";
import { fetchQuestions, getPreferences, savePreferences, unsubscribe, resubscribe } from "@/lib/api";

export default function Preferences() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | error
  const [questions, setQuestions] = useState([]);
  const [sub, setSub] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([fetchQuestions(), getPreferences(token)])
      .then(([qs, res]) => {
        setQuestions(qs);
        setSub(res.data);
        setAnswers(res.data.answers || {});
        setState("ready");
      })
      .catch(() => setState("error"));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await savePreferences(token, answers);
      toast.success("Preferences saved — your next letter will follow them.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't save just now. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const stop = async () => {
    try {
      await unsubscribe(token);
      setSub((s) => ({ ...s, status: "unsubscribed" }));
      toast.success("You're unsubscribed — gently.");
    } catch {
      toast.error("We couldn't do that just now. Please try again.");
    }
  };

  const resume = async () => {
    try {
      await resubscribe(token);
      setSub((s) => ({ ...s, status: "active" }));
      toast.success("Welcome back in.");
    } catch {
      toast.error("We couldn't do that just now. Please try again.");
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-moss animate-pulse">Opening your answers…</p>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-serif text-3xl text-ink mb-4">This link went quiet.</h1>
        <p className="text-moss max-w-md mb-8">
          We couldn't find your subscription link. It may be incomplete — every email from us
          carries a fresh one at the bottom.
        </p>
        <Link to="/" className="link-quiet">Back to the home page</Link>
      </div>
    );
  }

  return (
    <div data-testid="preferences-page">
      <header className="sticky top-0 z-40 bg-paper/90 backdrop-blur-md border-b border-sage-100">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="AuDHDSpaces home">
            <Logo size={30} />
            <Wordmark />
          </Link>
          <span className="text-sm text-moss hidden sm:block">{sub.email}</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 pt-16 pb-24">
        <Reveal>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink mb-5">Your answers</h1>
          <p className="text-moss mb-4">
            Change anything below and the letters will follow. Save when you're ready — or
            don't. The current answers suit you fine too.
          </p>
          {sub.status === "unsubscribed" && (
            <div data-testid="unsubscribed-notice" className="mt-6 bg-cream rounded-2xl px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="text-ink text-sm">You're currently unsubscribed. The letters are paused.</p>
              <button
                type="button"
                data-testid="resume-letters-button"
                onClick={resume}
                className="shrink-0 rounded-full bg-sage-700 px-6 py-2.5 text-sm font-semibold text-paper hover:bg-sage-900 transition-colors duration-300"
              >
                Resume the letters
              </button>
            </div>
          )}
          {sub.status === "pending" && (
            <div data-testid="pending-notice" className="mt-6 bg-cream rounded-2xl px-6 py-5">
              <p className="text-ink text-sm">
                One tap away: check your inbox for the "Yes, add me" email to start the letters.
              </p>
            </div>
          )}
        </Reveal>

        <div className="mt-14 flex flex-col gap-14">
          {questions.map((q, qi) => (
            <Reveal key={q.id} delay={Math.min(qi * 0.05, 0.2)}>
              <section>
                <p className="text-xs tracking-[0.18em] uppercase text-sage-700 mb-2">
                  Question {qi + 1}
                </p>
                <h2 className="font-serif text-2xl text-ink mb-2">{q.title}</h2>
                <p className="text-moss text-sm mb-5">{q.hint}</p>
                <div className="flex flex-col gap-3">
                  {q.options.map((o) => {
                    const selected = answers[q.id] === o.id;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        data-testid={`option-${q.id}-${o.id}`}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: o.id }))}
                        className={`w-full rounded-2xl border px-6 py-4 text-left text-base transition-colors duration-300 ${
                          selected
                            ? "bg-sage-700 border-sage-700 text-paper"
                            : "bg-white border-sage-200 text-ink hover:border-sage-500"
                        }`}
                      >
                        <span className="flex items-center justify-between gap-4">
                          <span>{o.label}</span>
                          <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
                              selected ? "border-cream bg-cream/20" : "border-sage-200"
                            }`}
                          >
                            {selected && <Check className="h-3 w-3 text-cream" strokeWidth={3} />}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 flex flex-col sm:flex-row items-center gap-5">
          <button
            type="button"
            data-testid="preferences-save-button"
            disabled={saving}
            onClick={save}
            className="rounded-full bg-sage-700 px-10 py-4 text-sm font-semibold text-paper hover:bg-sage-900 transition-colors duration-300 disabled:opacity-60"
          >
            {saving ? "Saving gently…" : "Save my answers"}
          </button>
          <button
            type="button"
            data-testid="preferences-unsubscribe-button"
            onClick={stop}
            className="text-sm text-[#A86454] underline underline-offset-4 decoration-[#A86454]/40 hover:decoration-[#A86454] transition-colors duration-300"
          >
            Or unsubscribe from the letters
          </button>
        </div>
      </main>
    </div>
  );
}
